import React from "react";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const contentSections = [
  {
    title: "Privacy Policy",
    content:
      'Effective Date: January 1st, 2025 Welcome to Lumination Ltd. Co. ("we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (www.rs-chain.com) or interact with our services. Please read this policy carefully to understand your rights and our practices regarding your personal information.',
  },
  {
    title: "1. Information We Collect",
    content:
      "We collect the following types of personal information when you use our services:\n● Personal Identifiers: Full name, phone number, email address, mailing address, and Discord username.\n● Transaction Information: Payment information for completing transactions.\n● Shipping Information: Addresses used to generate shipping labels and schedule pickups.",
  },
  {
    title: "2. How We Use Your Information",
    content:
      "Your personal information is used for the following purposes:\n● Account Verification: We use phone numbers and email addresses to verify your account and prevent duplicate accounts.\n● Shipping and Fulfillment: We use mailing addresses to generate shipping labels and coordinate pickups with shipping carriers.\n● Communications: We may use your email and phone number to notify you of payment statuses, delivery updates, and important account-related information. We may use your Discord username to authorize and verify access to our official server for communication purposes.",
  },
  {
    title: "3. Sharing Your Information with Third Parties",
    content:
      "We share your information with selected third parties only for essential business operations:\n● Shipping Carriers: Your shipping information may be shared with carriers such as FedEx, UPS, and USPS for order fulfillment.\n● Partner Platforms: For transparency in product sourcing, we may share limited data with platforms like Amazon.\n● Legal Disclosures: In cases of suspected illegal activity, such as selling inauthentic items, we may share relevant information with legal representatives and law enforcement authorities, as required.",
  },
  {
    title: "4. Your Rights",
    content:
      "Under the Texas Data Privacy and Security Act (TDPSA), you have the following rights:\n● Right to Access: You may request a copy of the personal data we hold about you.\n● Right to Correct: You may request corrections to any inaccurate information.\n● Right to Delete: You can request the deletion of your data, unless required for our legal obligations, such as tax and accounting.\n● Right to Opt-Out: You have the right to opt out of certain types of processing, such as direct marketing.\nTo exercise any of these rights, please contact us at support@rs-chain.com.",
  },
  {
    title: "5. Data Retention",
    content:
      "We retain personal data as long as necessary to fulfill our business purposes or comply with legal obligations. Unless deletion is requested, we keep your data for an indefinite period to maintain accurate records and for security purposes.",
  },
  {
    title: "6. Security Measures",
    content:
      "We implement industry-standard security measures to protect your data. Sensitive information, such as payment details, is encrypted to prevent unauthorized access. While we take reasonable precautions, no data transmission is entirely secure, and we cannot guarantee absolute security.",
  },
  {
    title: "7. Age Restrictions",
    content:
      "Our services are intended for users aged 18 and over. We do not knowingly collect information from minors. If you believe a minor has provided us with their data, please contact us to address and delete the information.",
  },
  {
    title: "8. Updates to This Policy",
    content:
      "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Your continued use of our services after changes are posted constitutes acceptance of the revised policy.",
  },
  {
    title: "9. Contact Us",
    content:
      "If you have any questions or concerns about this Privacy Policy, please contact us at: support@rs-chain.com.",
  },
];

const tnc = `
# RSC Privacy Policy

**Effective Date:** January 1st, 2025
**Last Updated:** July 8th, 2026

This Privacy Policy describes how Lumination Ltd Co, doing business as RSC ("RSC," "we," "us," or "our"), collects, uses, discloses, and protects information about you when you use our websites, member platform, partner portal, mobile experiences, Discord community, storefronts on third-party marketplaces, and related services (collectively, the "Services"), or when you sell products to us, purchase products from us, or otherwise interact with us.

By using the Services, you acknowledge this Privacy Policy. If you do not agree with it, please do not use the Services.

## 1. Who We Are and Scope

RSC operates a trading card and collectibles marketplace and distribution business, including a member sell-in program (through which members sell products to RSC and receive payouts), retail sales channels, and business-to-business partner services. This Policy covers all of these activities.

This Policy does not cover third-party platforms we operate on (such as Discord or third-party marketplaces), which have their own privacy policies, except with respect to information those platforms provide to us.

## 2. Information We Collect

### 2.1 Information you provide directly

- **Account information:** name, email address, username, password (stored in hashed form), phone number, mailing and shipping addresses, and account preferences.
- **Seller program information:** product listings and declarations, shipment details, tracking numbers, payout requests, and communications with our team.
- **Identity verification information:** as described in Section 5, identity verification is performed by our verification provider. We receive verification results - not your underlying documents.
- **Financial account information:** as described in Section 6, when you link a bank account for payouts we receive limited account details (such as account-holder name match results, institution name, account type, masked account number, and tokenized account references). We do not receive or store your online banking username or password.
- **Tax information:** where required by law (for example, IRS reporting thresholds), taxpayer identification information collected through secure means.
- **Communications:** messages, support tickets, emails, and other correspondence with us, including on Discord and marketplace messaging systems.
- **Photos and media you submit:** for example, photos of products, shipments, or damage claims.

### 2.2 Information collected automatically

- **Device and usage data:** IP address, browser type, device identifiers, operating system, pages viewed, referring URLs, timestamps, and interactions with the Services.
- **Cookies and similar technologies:** as described in Section 13.

### 2.3 Information from third parties

- Identity verification and financial data providers (see Sections 5 and 6).
- Shipping carriers (tracking and delivery data associated with your shipments).
- Payment and payout processors and banking partners (payment status and payout confirmation data).
- Third-party marketplaces where you purchase our products (order and dispute information).
- Fraud prevention sources, where lawful, in connection with protecting the integrity of our marketplace.

### 2.4 Information we create

- Transaction and inventory records, including serialized links between inbound shipments, inspection results, and outbound orders.
- Quality control and authentication records, including photographs and inspection documentation of products received from you or shipped to you.
- Fraud prevention records, including verification outcomes, account flags, and investigation documentation.

## 3. How We Use Information

We use information to:

- Provide, operate, and improve the Services, including processing sell-ins, orders, and payouts;
- Verify your identity and confirm bank account ownership before issuing payouts, as described in Sections 5 and 6;
- Authenticate products and prevent fraud, including tracing products to their source shipments, investigating tampered or counterfeit product, and enforcing our Seller Terms;
- Communicate with you about your account, transactions, and the Services;
- Comply with legal obligations, including tax reporting, responding to lawful requests, and cooperating with law enforcement;
- Protect the rights, property, and safety of RSC, our members, our customers, and the public;
- Analyze usage to improve the Services; and
- With your consent or at your direction, for any other purpose disclosed at the time of collection.

We do not sell your personal information, and we do not share it for cross-context behavioral advertising.

## 4. Legal Bases (where applicable)

Where required by applicable law, we process personal information on the following bases: performance of our contract with you (operating your account and paying you); compliance with legal obligations (tax, lawful requests); our legitimate interests (fraud prevention, marketplace integrity, security, service improvement) balanced against your rights; and your consent where we ask for it (including for identity verification and biometric processing as described below).

## 5. Identity Verification and Biometric Information

To protect our marketplace, members must complete a one-time identity verification before receiving payouts. Verification is performed by Plaid Inc. ("Plaid"), a third-party identity verification and financial data provider, not by RSC.

**What happens during verification:** You will be asked to photograph a government-issued ID and take a selfie. Plaid analyzes these to verify the document's authenticity and confirm that the selfie matches the ID. This process may involve the collection and processing of biometric identifiers or biometric information (such as facial geometry derived from your selfie) by Plaid, subject to your consent obtained during the flow.

**What RSC receives and stores:** RSC receives verification results - pass/fail status, match scores, and limited verified attributes (such as your verified name and date-of-birth-derived age status). RSC does not receive, store, or have access to your ID document images, your selfie, or any biometric identifiers or biometric templates. We store a one-way cryptographic hash derived from verified identity attributes solely to detect duplicate accounts; this hash cannot be reversed to reveal your identity document details.

**Retention and destruction of biometric data:** Biometric information collected during verification is retained and destroyed by Plaid in accordance with Plaid's policies and applicable law. Because RSC does not possess biometric identifiers, RSC does not retain them. For details on Plaid's handling of your information, see the Plaid End User Privacy Policy at https://plaid.com/legal/#end-user-privacy-policy.

By completing identity verification, you acknowledge and consent to Plaid's collection and processing of your information, including biometric information, as described in Plaid's End User Privacy Policy and in the consent presented during the verification flow.

## 6. Financial Account Information

When you link a bank account for payouts, you do so through Plaid Link. You provide your banking credentials directly to Plaid - RSC never sees or stores your online banking username or password.

Through Plaid, we receive: your bank account and routing numbers for payout processing; the account type and institution; a masked account reference; and account-ownership data (such as the account-holder name on file with your financial institution) used solely to confirm the account belongs to you. Payouts will only be issued to an account whose ownership matches your verified identity.

You may disconnect a linked account at any time by contacting us or through your account settings; note that a valid, matched account is required to receive payouts. You can also manage Plaid connections through Plaid Portal (my.plaid.com).

## 7. How We Share Information

We share personal information only as described below. We do not sell it.

- Service providers who process information on our behalf under contractual confidentiality and security obligations: identity verification and financial data services (Plaid), payment and payout processing and banking services, cloud hosting and infrastructure, communications and email delivery, analytics, customer support tooling, and tax preparation/reporting services.
- Shipping carriers to move products to and from you.
- Marketplaces and platforms where necessary to fulfill and support orders you place with us there.
- Professional advisors (lawyers, accountants, auditors, insurers) under confidentiality obligations.
- Law enforcement, regulators, and courts: we may disclose information where required by law, subpoena, or court order, and we may proactively report suspected fraud, product tampering, counterfeiting, or other unlawful activity to law enforcement, including sharing verification results, account records, shipment records, and investigation documentation. Sending tampered or counterfeit product to RSC may result in such disclosure.
- Business transfers: if RSC is involved in a merger, acquisition, financing, or sale of assets, information may be transferred as part of that transaction, subject to this Policy's protections.
- With your consent or at your direction.

## 8. Fraud Prevention and Marketplace Integrity

We operate an authentication and accountability program that includes unit-level serialization of inbound products, inspection and photographic documentation, identity verification, and bank-account ownership matching. Information processed under this program is used to trace products to source shipments, withhold payouts for products that fail authentication under our Seller Terms, remove accounts connected to tampered or counterfeit product, detect duplicate or evasive accounts, and support civil claims and criminal referrals. We retain records generated by this program for as long as necessary for these purposes, including for the duration of any investigation, claim, or limitation period.

## 9. Data Retention

We retain personal information only as long as necessary for the purposes described in this Policy, and then delete or de-identify it. Indicative retention periods:

- **Account information:** Life of the account + [3] years.
- **Transaction, payout, and tax records:** [7] years (legal/tax requirements).
- **Verification results, match scores, audit logs:** [7] years.
- **Identity document images and biometric data:** Not held by RSC (see Section 5; retained/destroyed by Plaid per its policy).
- **Fraud investigation records:** Duration of investigation/claims + applicable limitation periods.
- **Support communications:** [3] years.
- **Device/usage logs:** [12-24] months.

Where deletion is requested, some information may be retained where the law permits or requires it, including for fraud prevention, tax, and legal claims.

## 10. Security

We maintain administrative, technical, and physical safeguards appropriate to the sensitivity of the information we handle, including encryption of data in transit and at rest, role-based least-privilege access controls, multi-factor authentication for administrative access, vendor security review, and audit logging. Our architecture is built on data minimization: the most sensitive artifacts (identity documents, biometrics, banking credentials) are handled by specialized providers and never stored on RSC systems. No method of transmission or storage is completely secure; if we become aware of a breach affecting your personal information, we will notify you and regulators as required by law.

## 11. Your Privacy Rights

Depending on where you live (including California, Texas, Colorado, Connecticut, Virginia, Utah, Oregon, Montana, and other states with comprehensive privacy laws), you may have the right to:

- Know/Access the personal information we hold about you, including categories, sources, purposes, and third parties with whom it is shared;
- Correct inaccurate personal information;
- Delete personal information, subject to legal exceptions (we may retain information needed for fraud prevention, security, legal compliance, and defense of claims);
- Portability - receive a copy in a usable format;
- Opt out of sale, sharing for cross-context behavioral advertising, and certain profiling (RSC does not sell or share personal information for these purposes);
- Non-discrimination for exercising your rights; and
- Appeal a refusal, where state law provides an appeal right.

To exercise these rights: email privacy@lumination.us. We will verify your identity before acting on a request (for security, verification-related requests may require re-confirmation through our verification provider) and respond within the time required by applicable law. You may use an authorized agent where the law allows; we will require proof of authorization.

California notice: the categories of personal information we collect are described in Section 2; purposes in Section 3; disclosure categories in Section 7. We do not sell personal information or share it for cross-context behavioral advertising, and we have not done so in the preceding 12 months. We do not use or disclose sensitive personal information for purposes other than those permitted under the CCPA.

## 12. Minors

The Services are not directed to children under 13, and we do not knowingly collect personal information from children under 13. If we learn we have done so, we will delete it.

Participation in the RSC seller program, withdrawals, and identity verification require you to be at least 18 years old (or the age of majority in your jurisdiction). If identity verification indicates that a member is under 18, withdrawal capability will be suspended and we will contact the member regarding available options. Parents or guardians who believe their child has provided personal information to us may contact us at privacy@lumination.us.

## 13. Cookies and Tracking Technologies

We use cookies and similar technologies for essential site functions (authentication, session management, security), preferences, and analytics that help us understand how the Services are used. You can control cookies through your browser settings; disabling essential cookies may impair the Services. Where required, we honor opt-out preference signals (such as Global Privacy Control) for applicable tracking. We do not use third-party advertising cookies on the member platform.

## 14. Third-Party Services and Links

The Services link to or operate alongside third-party platforms - including Discord, third-party marketplaces, shipping carriers, and Plaid - each governed by its own privacy policy. We encourage you to review those policies. This Policy does not apply to information you provide directly to third parties.

## 15. International Users

RSC is based in the United States and the Services are operated from the United States. If you access the Services from outside the U.S., you understand that your information will be transferred to, stored, and processed in the United States, where privacy laws may differ from those of your jurisdiction.

## 16. Changes to This Policy

We may update this Policy from time to time. If we make material changes, we will notify you by email, in-app notice, or a prominent posting before the changes take effect, and we will update the "Last Updated" date above. Material changes to how we handle verification or financial data will require renewed acknowledgment at your next login.

## 17. Contact Us

Lumination Ltd Co (DBA RSC)

Email: privacy@lumination.us
`;

const secondTnc = `## Terms and Conditions

These terms and conditions outline the rules and regulations for the use of RSC's Website and Discord Server. (https://rs-chain.com)

By accessing this website and/or Discord server, we assume you accept these terms and conditions in full. Do not continue to use RSC's website or Discord server if you do not accept all of the terms and conditions stated on this page.

The following terminology applies to these Terms and Conditions, Privacy Statement, and Disclaimer Notice, as well as any or all Agreements: "Client," "You," and "Your" refer to you, the person accessing this website or Discord server and accepting the Company's terms and conditions. "The Company," "Ourselves," "We," "Our," and "Us" refer to our Company, RSC. "Party," "Parties," or "Us" refer to both the Client and ourselves, or either the Client or ourselves. All terms refer to the offer, acceptance, and consideration of payment necessary to undertake the process of our assistance to the Client in the most appropriate manner, whether by formal meetings, digital communications, or any other means, for the express purpose of meeting the Client's needs in respect to the provision of the Company's stated services/products, in accordance with and subject to the prevailing law of the United States. Any use of the above terminology or other words in the singular, plural, capitalization, and/or he/she or they are taken as interchangeable and therefore as referring to the same.

---

### 1. Use of Our Services

1.1 By using RSC's website and Discord server, you confirm that you are at least 18 years old or have permission from a legal guardian to engage in transactions with us.
1.2 You agree to provide accurate information when requesting quotes, selling items, and conducting any transactions on our platform.
1.3 Any attempt to exploit the system, manipulate prices, or engage in fraudulent activity will result in an immediate ban and potential legal action.

### 2. Item Listings and Sales

2.1 All items sold through RSC must be authentic, accurately described, and in the agreed condition.
2.2 It is your responsibility to ensure the items are shipped within the agreed-upon timeframe.
2.3 We reserve the right to refuse or cancel any transaction if we suspect fraudulent behavior or misrepresentation of items.

### 3. Payments and Payouts

3.1 Payments for accepted items will be processed within the timeframe stated at the time of agreement.
3.2 Payouts will be made via the chosen payment method, and any fees associated with the transaction may be deducted.
3.3 Chargebacks, disputes, or fraudulent activity involving payments will result in suspension and potential legal action.

### 4. Prohibited Items

4.1 The sale of stolen, counterfeit, restricted, or prohibited items is strictly forbidden.
4.2 Any attempt to sell such items will result in immediate termination of your account and potential legal consequences.
4.3 RSC reserves the right to update the list of prohibited items at any time.

### 5. Shipping & Tracking

5.1 You must ship items using the correct method and provide valid tracking information.
5.2 Failure to ship items as agreed may result in delays, loss of payment, or banning from our platform.
5.3 RSC is not responsible for lost, damaged, or undelivered shipments unless otherwise specified.

### 6. User Conduct & Community Guidelines

6.1 Respectful communication is required at all times within the Discord server and platform.
6.2 Harassment, threats, hate speech, or any form of misconduct will result in an immediate ban.
6.3 Advertising or promoting third-party services without permission is prohibited.

### 7. Disputes & Resolutions

7.1 If a dispute arises, users must contact RSC Support for resolution before taking external action.
7.2 RSC reserves the right to make final decisions on disputes based on available evidence.
7.3 If legal action is required, disputes will be resolved under the jurisdiction of Texas.

### 8. Account Termination

8.1 RSC reserves the right to suspend or terminate accounts at our sole discretion for violations of these terms.
8.2 Users who are banned or suspended will forfeit any pending transactions or unpaid balances.
8.3 Appeals for reinstatement may be considered but are not guaranteed.

### 9. Cookies

9.1 RSC uses cookies to enhance user experience, improve site functionality, and analyze traffic.
9.2 By using our website, you consent to the use of cookies in accordance with our Privacy Policy.
9.3 You can manage or disable cookies through your browser settings, but some features of our platform may not function properly without them.

### 10. Modifications to Terms & Conditions

10.1 RSC reserves the right to update these terms at any time without prior notice.
10.2 Continued use of the platform after modifications indicates acceptance of the new terms.

### 11. Contact Information

For questions or concerns regarding these Terms and Conditions, please contact us at: support@rs-chain.com.

---

By using RSC's website and Discord server, you acknowledge that you have read, understood, and agreed to these Terms & Conditions.
`;

const TermsAndCondition = () => {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>
      <style>{`
        .tnc-markdown p { color: rgba(255,255,255,0.85); margin-bottom: 16px; font-size: 15px; line-height: 1.6; }
        .tnc-markdown li { color: rgba(255,255,255,0.85); margin-bottom: 6px; font-size: 15px; line-height: 1.6; }
        .tnc-markdown h1 { color: #fff; margin-top: 28px; margin-bottom: 16px; font-weight: 700; font-size: 26px; }
        .tnc-markdown h2 { color: #fff; margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 21px; }
        .tnc-markdown h3 { color: #fff; margin-top: 18px; margin-bottom: 8px; font-weight: 700; font-size: 17px; }
        .tnc-markdown a { color: #00ADB5; }
        .tnc-markdown strong { color: rgba(255,255,255,0.92); }
        .tnc-markdown hr { border: none; border-top: 1px solid rgba(255,255,255,0.12); margin: 24px 0; }
        .tnc-markdown table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        .tnc-markdown th, .tnc-markdown td { border: 1px solid rgba(255,255,255,0.12); padding: 8px; text-align: left; vertical-align: top; color: rgba(255,255,255,0.85); }
        .tnc-markdown ul, .tnc-markdown ol { padding-left: 24px; margin-bottom: 16px; }
      `}</style>
      <button
        type="button"
        onClick={() => navigate("/")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.54)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          marginBottom: 24,
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <div className="tnc-markdown" style={{ marginBottom: 40 }}>
        <ReactMarkdown>{secondTnc}</ReactMarkdown>
      </div>

      <div className="tnc-markdown">
        <ReactMarkdown>{tnc}</ReactMarkdown>
      </div>
    </div>
  );
};

export default TermsAndCondition;
